import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import {
  StudentFeedbackApiResponse,
  SingleFeedbackApiResponse,
  FeedbackDeleteApiResponse,
  FeedbackEligibilityApiResponse,
  CurrentWeekInfoApiResponse,
  SaveFeedbackPayload,
  UpdateFeedbackPayload,
  DeleteFeedbackPayload,
} from "@src/pages/batches/student-feedback/types";

export interface GetFeedbackHistoryParams {
  batchId: string;
  studentId: string;
}

export interface GetWeeklyFeedbackParams {
  batchId: string;
  studentId: string;
  week?: string;
  weekNumber?: number;
}

export const StudentFeedbackApiSlice = createApi({
  reducerPath: "studentFeedbackApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["StudentFeedback"],
  endpoints: (builder) => ({
    getStudentFeedbackHistory: builder.query<
      StudentFeedbackApiResponse,
      GetFeedbackHistoryParams
    >({
      query: ({ batchId, studentId }) => ({
        url: `feedback/student/${studentId}?batchId=${encodeURIComponent(batchId)}`,
        method: "GET",
      }),
      providesTags: (result, error, { studentId }) => [
        { type: "StudentFeedback", id: studentId },
        { type: "StudentFeedback", id: "LIST" },
      ],
    }),

    getTrainerStudentFeedbackHistory: builder.query<
      StudentFeedbackApiResponse,
      GetFeedbackHistoryParams
    >({
      query: ({ batchId, studentId }) => ({
        url: `feedback/trainer/student/${studentId}?batchId=${encodeURIComponent(batchId)}`,
        method: "GET",
      }),
      providesTags: (result, error, { studentId }) => [
        { type: "StudentFeedback", id: studentId },
        { type: "StudentFeedback", id: "LIST" },
      ],
    }),

    getWeeklyFeedbackForStudent: builder.query<
      SingleFeedbackApiResponse,
      GetWeeklyFeedbackParams
    >({
      query: ({ batchId, studentId, week }) => ({
        url: `feedback/student/${studentId}?batchId=${encodeURIComponent(batchId)}${
          week ? `&week=${encodeURIComponent(week)}` : ""
        }`,
        method: "GET",
      }),
      providesTags: (result, error, { studentId, week }) => [
        { type: "StudentFeedback", id: `${studentId}_${week || "all"}` },
      ],
    }),

    getFeedbackEligibility: builder.query<
      FeedbackEligibilityApiResponse,
      GetFeedbackHistoryParams
    >({
      query: ({ batchId, studentId }) => ({
        url: `feedback/eligibility?studentId=${encodeURIComponent(studentId)}&batchId=${encodeURIComponent(batchId)}`,
        method: "GET",
      }),
      providesTags: (result, error, { studentId }) => [
        { type: "StudentFeedback", id: `eligibility_${studentId}` },
      ],
    }),

    getCurrentWeekInfo: builder.query<CurrentWeekInfoApiResponse, void>({
      query: () => ({
        url: `feedback/current-week`,
        method: "GET",
      }),
    }),

    saveStudentFeedback: builder.mutation<
      SingleFeedbackApiResponse,
      SaveFeedbackPayload
    >({
      query: (payload) => ({
        url: `feedback`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: "StudentFeedback", id: studentId },
        { type: "StudentFeedback", id: "LIST" },
      ],
    }),

    updateStudentFeedback: builder.mutation<
      SingleFeedbackApiResponse,
      UpdateFeedbackPayload
    >({
      query: (payload) => ({
        url: `feedback/${payload.feedbackId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: "StudentFeedback", id: studentId },
        { type: "StudentFeedback", id: "LIST" },
      ],
    }),

    deleteStudentFeedback: builder.mutation<
      FeedbackDeleteApiResponse,
      DeleteFeedbackPayload
    >({
      query: ({ feedbackId }) => ({
        url: `feedback/${feedbackId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { studentId }) => [
        { type: "StudentFeedback", id: studentId },
        { type: "StudentFeedback", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetStudentFeedbackHistoryQuery,
  useLazyGetStudentFeedbackHistoryQuery,
  useGetTrainerStudentFeedbackHistoryQuery,
  useLazyGetTrainerStudentFeedbackHistoryQuery,
  useGetWeeklyFeedbackForStudentQuery,
  useLazyGetWeeklyFeedbackForStudentQuery,
  useGetFeedbackEligibilityQuery,
  useLazyGetFeedbackEligibilityQuery,
  useGetCurrentWeekInfoQuery,
  useSaveStudentFeedbackMutation,
  useUpdateStudentFeedbackMutation,
  useDeleteStudentFeedbackMutation,
} = StudentFeedbackApiSlice;
